"use client";

import { agentsFiltersAtom } from "@/app/(saas)/(app)/business-brokers/_components/filters/state";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Icon } from "@/components/ui/icon";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Separator } from "@/components/ui/separator";
import { api } from "@/lib/api-client";
import type { Language } from "@/lib/types";
import { cn } from "@/lib/utils";
import { useAtom } from "jotai";
import { useState } from "react";

export function AgentListLanguageFilter() {
  const [open, setOpen] = useState(false);
  const [filters, setFilters] = useAtom(agentsFiltersAtom);

  const { data } = api.common.languages.get.useQuery(undefined, {
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const isActive = filters.languageIds.length > 0;

  function handleLanguageChange(language: Language | null) {
    setFilters((prev) => ({
      ...prev,
      languageIds: language ? [language.id] : [],
    }));
    setOpen(false);
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            "h-9 rounded-full text-sm px-4 border transition-colors",
            isActive
              ? "bg-[var(--venturu-green-soft)] border-[var(--venturu-green-soft)] text-[var(--venturu-black)]"
              : "border-[var(--venturu-gray-100)] hover:border-[var(--venturu-gray-300)]",
          )}
        >
          Language
          <Icon icon="ChevronDown" className="size-3.5 ml-1" />
          {isActive && (
            <button
              type="button"
              className="ml-1 hover:bg-black/5 rounded-full p-0.5"
              onClick={(e) => {
                e.stopPropagation();
                handleLanguageChange(null);
              }}
            >
              <Icon icon="XClose" className="size-3" />
            </button>
          )}
        </Button>
      </PopoverTrigger>

      <PopoverContent align="start" className="w-56">
        <Command>
          <CommandInput
            placeholder="Search languages..."
            className="h-9 border-0 focus:ring-0 focus:border-0"
            containerClassName="border rounded-md"
          />
          <div className="py-2">
            <Separator />
          </div>
          <CommandList>
            <CommandGroup>
              <CommandItem
                value="all"
                onSelect={() => handleLanguageChange(null)}
              >
                All languages
              </CommandItem>
              {(data?.languages ?? []).map((language) => (
                <CommandItem
                  key={language.id}
                  value={language.name}
                  onSelect={() => handleLanguageChange(language)}
                >
                  {language.name}
                  <Icon
                    icon="CheckSolid"
                    className={cn("ml-auto", {
                      "opacity-0": !filters.languageIds.includes(language.id),
                    })}
                  />
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

AgentListLanguageFilter.displayName = "AgentListLanguageFilter";
